import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import type { RxDocument } from 'rxdb';
import type { Tag } from '@flintmint/core-shared/rxdb';
import type { SearchResult } from '@/services/search-service/types';
import type { MenuItemProps } from '@flintmint/web-shared/components/menu-item';

import { useTagDetachFromAll, useTagPatch } from '@/hooks/rxdb-models';
import { useItemActions } from '@/hooks/use-item-actions';
import { useUniqueDocument } from '@/hooks/use-unique-document';

import { AssignTag } from '@/components/assign-tag';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Popover } from '@flintmint/web-shared/components/popover';
import { LastSyncEditTime } from '@/components/last-sync-edit-time';
import { ColorPicker } from '@flintmint/web-shared/components/color-picker';
import { InlineInput } from '@flintmint/web-shared/components/inline-input';

import styles from './tag-header.module.scss';

export const TagHeader = (props: TagHeaderProps) => {
  const { tag, taggedDocs } = props;

  const [tagName, setTagName] = useState(tag.name);

  const { mutate: patchTag } = useTagPatch();
  const { mutate: detachTagFromAll } = useTagDetachFromAll();
  const { configureDeleteOption } = useItemActions();
  const { isUnique, errorMessage } = useUniqueDocument(tagName, 'tags', tag.id);

  const updateTagName = () => {
    !tagName && setTagName(tag.name);

    if (isUnique && tagName && tag.name !== tagName) {
      return patchTag(tag, {
        name: tagName || tag.name,
      });
    }
  };

  const menu: MenuItemProps[] = [
    {
      danger: true,
      // numDocs can be transiently negative (see tag-handler.ts), so test for a positive count.
      disabled: !(tag.numDocs > 0),
      icon: 'unlink-04',
      name: 'Detach From Everything',
      showConfirmation: true,
      deleteData: {
        type: 'danger',
        message: 'Are you sure you want to detach this tag from all items? This action can’t be undone.',
        title: <>Detach From Everything</>,
        primaryButton: {
          children: 'Detach',
          onClick: async (onClose) => {
            await detachTagFromAll(tag.id);
            onClose?.();
          },
        },
      },
    },
    configureDeleteOption([{ ...tag.toJSON(), type: 'tags' }]),
  ];

  const animateHeight = {
    initial: { height: 0, opacity: 0 },
    animate: { height: 'auto', opacity: 1 },
    exit: { height: 0, opacity: 0 },
    transition: { duration: 0.25 },
  };

  return (
    <ul className={styles.tagHeader}>
      <li className={styles.tagNameHolder}>
        <div className={styles.tagName}>
          <ColorPicker color={tag.color} indicatorSize="big" onChange={(color) => patchTag(tag, { color })} />
          <InlineInput
            value={tagName}
            data-testid="inline-input"
            className={styles.inlineInput}
            onChange={(e) => setTagName(e.target.value)}
            onBlur={updateTagName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                return updateTagName();
              }
            }}
          />
          <Popover menu={menu} placement="bottom" offset={[0, 6]}>
            <Icon type="more-horizontal" className={styles.menu} data-testid="tag-workspace-menu" />
          </Popover>
        </div>
        {!!taggedDocs.length && <AssignTag tagId={tag.id} taggedDocs={taggedDocs} appearance="plain" />}
      </li>
      <AnimatePresence mode="wait" initial={false}>
        {errorMessage ? (
          <motion.li key="error" {...animateHeight}>
            {errorMessage}
          </motion.li>
        ) : (
          <motion.li key="edited-date" {...animateHeight}>
            <LastSyncEditTime lastEditTime={tag.updatedAt} />
          </motion.li>
        )}
      </AnimatePresence>
    </ul>
  );
};

export interface TagHeaderProps {
  tag: RxDocument<Tag>;
  taggedDocs: SearchResult[];
}
